import React, { useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { MULTI_SELECT_SEPARATOR, multiSelectOptionValue } from '@repo/lib';
import { useMultiSelectRules } from './useMultiSelectRules';

export type MultiSelectOption = { _id?: string; label: string; value?: string | null };

type Props = {
  id: string;
  options: MultiSelectOption[];
  /** The chosen options' values; the panel shows their labels. */
  value: string[];
  onChange: (values: string[]) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  /** Fewest options a given answer must have; an optional field still accepts none. */
  min?: number;
  /** Most options the customer may choose; the rest are disabled once reached. */
  max?: number;
  className?: string;
};

const DEFAULT_PLACEHOLDER = 'Select an option';

// One option row (py-2 plus a line of text) and the panel's own padding and
// border, used to estimate the panel height before it has rendered.
const ROW_HEIGHT = 44;
const PANEL_CHROME = 12;
const PANEL_MAX_HEIGHT = 320;

/**
 * The panel is positioned absolutely rather than portalled so it stays inside
 * the themed checkout wrapper and keeps the merchant's CSS variables, the same
 * reason the address autocomplete does not portal. The trade-off is that
 * nothing flips it for us, so the direction is measured here: it opens above
 * the button when it would not fit below and there is more room above.
 */
function useOpensUpwards(
  open: boolean,
  buttonRef: React.RefObject<HTMLButtonElement | null>,
  optionCount: number,
) {
  const [opensUpwards, setOpensUpwards] = useState(false);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current || typeof window === 'undefined') return;
    const rect = buttonRef.current.getBoundingClientRect();
    const estimated = Math.min(optionCount * ROW_HEIGHT + PANEL_CHROME, PANEL_MAX_HEIGHT);
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    setOpensUpwards(below < estimated && above > below);
  }, [open, buttonRef, optionCount]);

  return opensUpwards;
}

type PanelProps = Props & { open: boolean };

function MultiSelectPanel({
  id,
  options,
  value,
  onBlur,
  placeholder,
  required,
  min = 0,
  max,
  className,
  open,
}: PanelProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const opensUpwards = useOpensUpwards(open, buttonRef, options.length);
  const selected = options.filter(option => value.includes(multiSelectOptionValue(option)));
  const closedText = selected.map(option => option.label).join(MULTI_SELECT_SEPARATOR);
  const { requiredInputRef, atMax } = useMultiSelectRules({
    selectedCount: selected.length,
    required,
    min,
    max,
  });

  return (
    <div className="relative" data-testid={`multi-select-${id}`}>
      <ListboxButton
        ref={buttonRef}
        id={id}
        className={clsx(
          'cp_form-select cp_form-multiselect block w-full select-none text-left flex items-center justify-between gap-2',
          className,
        )}
        onBlur={onBlur}
      >
        <span className={clsx('truncate', !closedText && 'cp_form-multiselect-placeholder')}>
          {closedText || placeholder || DEFAULT_PLACEHOLDER}
        </span>
        <ChevronDownIcon
          className="cp_form-multiselect-chevron h-4 w-4 shrink-0"
          aria-hidden="true"
        />
      </ListboxButton>

      {/* The browser validates this instead of the button, so a required
          multi-select blocks submit with the same native message as every
          other field. It mirrors the selection and is never focusable by tab. */}
      <input
        ref={requiredInputRef}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        required={required}
        value={closedText}
        onChange={() => {}}
        data-testid={`multi-select-${id}-required`}
      />

      <ListboxOptions
        modal={false}
        data-placement={opensUpwards ? 'top' : 'bottom'}
        className={clsx(
          'cp_form-multiselect-options',
          'absolute z-50 left-0 right-0 overflow-hidden',
          opensUpwards ? 'bottom-full mb-1' : 'top-full mt-1',
          'border border-[var(--cp_checkout-form-fields-input-border-color)]',
          'bg-[var(--cp_checkout-form-fields-input-background-color)]',
          'text-[var(--cp_checkout-form-fields-input-color)]',
          'rounded-[var(--cp_checkout-form-fields-input-border-radius)]',
          'shadow-lg focus:outline-none',
        )}
      >
        <div className="max-h-80 overflow-y-auto py-1">
          {options.map(option => (
            <ListboxOption
              key={option._id ?? option.label}
              value={multiSelectOptionValue(option)}
              disabled={atMax && !value.includes(multiSelectOptionValue(option))}
              className={({ focus, disabled }) =>
                clsx(
                  'cp_form-multiselect-option flex cursor-pointer select-none items-center gap-2 px-3 py-2 transition-colors',
                  disabled && 'cursor-not-allowed opacity-40',
                  focus &&
                    'bg-[color-mix(in_oklab,var(--cp_checkout-form-fields-input-color)_8%,transparent)]',
                )
              }
            >
              {({ selected: isSelected }) => (
                <>
                  <span
                    className="flex h-4 w-4 shrink-0 items-center justify-center"
                    data-selected={isSelected ? 'true' : 'false'}
                  >
                    {isSelected && (
                      <CheckIcon className="cp_form-multiselect-check h-4 w-4" aria-hidden="true" />
                    )}
                  </span>
                  <span className="truncate">{option.label}</span>
                </>
              )}
            </ListboxOption>
          ))}
        </div>
      </ListboxOptions>
    </div>
  );
}

/**
 * A dropdown that takes several answers. Closed, it is styled exactly like
 * the native dropdown (same `cp_form-select` class, wrapped by the caller in
 * the field-input StyledElement). Open, it draws its own panel instead of the
 * browser's, which is what a native `<select multiple>` cannot do.
 */
export default function MultiSelectField(props: Props) {
  const { options, value, onChange } = props;

  const handleChange = (values: string[]) => {
    onChange(
      options.map(multiSelectOptionValue).filter(optionValue => values.includes(optionValue)),
    );
  };

  return (
    <Listbox value={value} onChange={handleChange} multiple>
      {({ open }) => <MultiSelectPanel {...props} open={open} />}
    </Listbox>
  );
}
